/**
 * Sibling-code groups for the inspector dynamic exception route.
 *
 * A sibling group is a set of related exception codes that share the
 * same row shape and benefit from in-page navigation between them.
 * Examples:
 * - the negative-stock pair (business roll-up vs per-outlet detail);
 * - the product-pricing health checks (avg cost zero, missing price, etc.);
 * - the product-setup quality checks (no category, Xero account error, etc.).
 *
 * The route renders these as a synthetic `IssueCardStrip` whose card
 * clicks navigate to the sibling code's URL. Strictly opt-in: a code
 * not listed here belongs to no sibling group and renders no strip.
 * Adding a new exception code does not automatically join a group; the
 * group must be edited here.
 *
 * No backend wiring. Each sibling code already has its own route, its
 * own data, its own count entry under ``inspectorApi.getXxxExceptionTypes``.
 * The strip just navigates between the URLs and reads counts off the
 * existing types-list response.
 */
import React from 'react'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import StoreIcon from '@mui/icons-material/Store'
import MoneyOffIcon from '@mui/icons-material/MoneyOff'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import PriceCheckIcon from '@mui/icons-material/PriceCheck'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import BedtimeIcon from '@mui/icons-material/Bedtime'
import SyncProblemIcon from '@mui/icons-material/SyncProblem'

export interface SiblingCode {
  code: string
  // Chip-strip wording (short, no domain prefix) - intentionally tuned
  // for the switcher and may diverge from the server's ``name_short`` on
  // the same code. Hardcoded so chips render instantly, before the
  // types-list fetch resolves.
  label: string
  // Icon rendered inside the issue card. Optional - the card renders
  // without one when omitted.
  icon?: React.ReactNode
}

export interface SiblingGroup {
  id: string
  // The frontend URL segment for the data domain (matches DOMAIN_KEY_MAP
  // values in DynamicExceptionRoute). Used by the host route to fetch
  // the per-code counts via the matching ``getXxxExceptionTypes`` call.
  domainKey: 'sales' | 'stock-management' | 'product-setup' | 'product-pricing' | 'systems' | 'client' | 'accounting'
  codes: SiblingCode[]
}

const SIBLING_GROUPS: SiblingGroup[] = [
  {
    id: 'stock-negative',
    domainKey: 'stock-management',
    codes: [
      {
        code: 'ex-inv-ls-negative-stock-business',
        label: 'Business view',
        icon: React.createElement(WarehouseIcon),
      },
      {
        code: 'ex-inv-ls-negative-stock-outlet',
        label: 'Outlet view',
        icon: React.createElement(StoreIcon),
      },
    ],
  },
  {
    id: 'product-pricing-health',
    domainKey: 'product-pricing',
    codes: [
      {
        code: 'ex-inv-ls-avg-cost-zero',
        label: 'Avg cost zero',
        icon: React.createElement(MoneyOffIcon),
      },
      {
        code: 'ex-inv-ls-avg-cost-over-retail',
        label: 'Cost > retail',
        icon: React.createElement(TrendingDownIcon),
      },
      {
        code: 'ex-inv-ls-missing-price',
        label: 'Missing price',
        icon: React.createElement(PriceCheckIcon),
      },
      {
        code: 'ex-inv-ls-noninv-has-avg-cost',
        label: 'Non-inv has cost',
        icon: React.createElement(Inventory2Icon),
      },
      {
        code: 'ex-inv-ls-supplier-over-retail',
        label: 'Supplier > retail',
        icon: React.createElement(LocalShippingIcon),
      },
    ],
  },
  {
    id: 'product-setup-quality',
    domainKey: 'product-setup',
    // Excludes the duplicates code (``ex-product-ls-duplicate``): it has
    // a comparison/grouped row shape with an expandable detail view, so
    // it doesn't share the simple product-row shape the other setup
    // configs use. ``ex-inv-ls-xero-account-error`` used to live here
    // too; it's now in the accounting domain so the sibling switcher
    // would cross domains. Reviewers can still navigate to it from the
    // Accounting tab.
    codes: [
      {
        code: 'ex-inv-ls-no-category',
        label: 'No category',
        icon: React.createElement(FolderOutlinedIcon),
      },
      {
        code: 'ex-product-ls-variant-group-dormant',
        label: 'Variant group dormant',
        icon: React.createElement(BedtimeIcon),
      },
      {
        code: 'ex-product-ls-supplier-variant-sold',
        label: 'Supplier variant sold',
        icon: React.createElement(SyncProblemIcon),
      },
    ],
  },
]

const CODE_TO_GROUP: Map<string, SiblingGroup> = new Map()
for (const group of SIBLING_GROUPS) {
  for (const member of group.codes) {
    CODE_TO_GROUP.set(member.code, group)
  }
}

/**
 * Return the sibling group an exception code belongs to, or null if it
 * stands alone. Callers should treat null as "render no switcher" -
 * the existing single-code rendering path applies.
 */
export const getSiblingGroupForCode = (code: string | undefined | null): SiblingGroup | null => {
  if (!code) return null
  return CODE_TO_GROUP.get(code) ?? null
}
